
import { useEffect, useState } from "react";
import { RootState } from "../../../../reducers";
import { useSelector } from "react-redux";
import {
  SwitchPanel,
  StatsInfoPanel,
  StatsInfoTitle,
  ChartPanel,
  OptionsPanel,
  OptionItem,
} from "./styles";
import { WarningMessage } from "../../../Results/styles";
import InfoComponent from "../../../Info";
import { showTypeName, getCompetitionName } from "../../../../utils";
import {
  ResponsiveContainer,
  ComposedChart,
  Cell,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ReferenceLine,
} from "recharts";
import Switch from "react-js-switch";
import { PlayerDetailType } from "../../../../types/playerDetail";
import {
  getFillColor,
  renderLegend,
  CustomTooltip,
  CustomizedXAxisTick,
  SelectionHandicap,
  SquareDot,
} from "./utils";
import { getBasedOn } from "../../../../utils";
import {
  InfoModalContainer,
} from "../../../Info/styles";

const { meanBy } = require("lodash");

interface Props {
  showPrevLines: boolean;
  chartBasedOn: string;
  data: PlayerDetailType[];
  selectionHandicap: number;
  handleOnClick: (type: string) => void;
  handleOnSwitch: () => void;
}

const defaultSwitchStyle = { on: "#ddd", off: "#ddd" };

function Chart({
  showPrevLines,
  chartBasedOn,
  data,
  selectionHandicap,
  handleOnClick,
  handleOnSwitch,
}: Props) {
  const [infoOpen, setInfoOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const filterStore = useSelector((state: RootState) => state.filter);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  const averageValue = meanBy(data, "value");
  const chartData = data
    ? data.map((i) => {
      return {
        ...i,
        xAxisData: `(${i.side.charAt(0).toUpperCase()}),${i.opponentCode}`,
      };
    })
    : null;

  const getName = (name: string) => {
    if (windowWidth > 425) { // mobile size
      return name;
    }
    switch (name) {
      case "Last 5 Games":
        return "L5";
      case "Last 10 Games":
        return "L10";
      case "Last 20 Games":
        return "L20";
      case "Head To Head":
        return "H2H";
      case "Home/Away":
        return "H/A";
      default:
        return name;
    }
  }

  const renderStatsInfo = () => {
    if (chartData && chartData.length > 0) {
      return (
        <StatsInfoPanel>
          <div>
            <StatsInfoTitle>
              <b>
                {showTypeName(filterStore.market, [
                  ...filterStore.configData.markets,
                  ...filterStore.configData.playerMarkets[
                  getCompetitionName(filterStore)
                  ],
                ])}
              </b>
            </StatsInfoTitle>
            <div>
              <span>
                {showTypeName(
                  chartBasedOn,
                  getBasedOn(
                    filterStore.competitions[0],
                    filterStore.configData.basedOn
                  )
                )}
              </span>
              &nbsp;
              <span>avg - {averageValue.toFixed(2)}</span>
            </div>
          </div>
          <SwitchPanel>
            {infoOpen ? <InfoModalContainer>
              <b>Shows previous betting lines for each game</b>
            </InfoModalContainer> : null
            }
            <InfoComponent
              show={infoOpen}
              handleOnOpen={() => {
                setInfoOpen(true);
              }}
              handleOnClose={() => {
                setInfoOpen(false);
              }}
            />
            <span>Prev. Lines</span>
            &nbsp;&nbsp;
            <Switch
              size={30}
              value={showPrevLines}
              onChange={() => {
                handleOnSwitch();
              }}
              color={showPrevLines ? "#1b3d8c" : "#fff"}
              borderColor={defaultSwitchStyle}
              backgroundColor={defaultSwitchStyle}
            />
          </SwitchPanel>
        </StatsInfoPanel>
      );
    }
  };

  const renderChart = () => {
    if (chartData && chartData.length > 0) {
      return (
        <ChartPanel>
          <ResponsiveContainer>
            <ComposedChart
              data={chartData}
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: -25,
              }}
            >
              <CartesianGrid stroke="#f5f5f5" />
              <YAxis />
              {chartData.length < 12 ? (
                <XAxis
                  dataKey="xAxisData"
                  tick={<CustomizedXAxisTick />}
                  interval={0}
                />
              ) : (
                <Legend content={renderLegend(chartData)} />
              )}
              <Tooltip
                content={
                  <CustomTooltip
                    showPrevLines={showPrevLines}
                    selectionHandicap={selectionHandicap}
                  />
                }
                position={{ y: 20 }}
              />
              <Bar dataKey="value">
                {data.map((entry, index) => (
                  <Cell
                    cursor="pointer"
                    fill={getFillColor(
                      showPrevLines,
                      selectionHandicap,
                      entry.value,
                      entry.handicap
                    )}
                    key={`cell-${index}`}
                  />
                ))}
              </Bar>
              {showPrevLines && (
                <Line
                  type="linear"
                  dataKey="handicap"
                  stroke="#bfbfbf"
                  strokeWidth={2}
                  dot={<SquareDot />}
                />
              )}
              {
                selectionHandicap > 0 && <ReferenceLine
                  y={selectionHandicap}
                  stroke="#1b3d8c"
                  strokeWidth={2}
                >
                  <Label
                    content={
                      <SelectionHandicap selectionHandicap={selectionHandicap} />
                    }
                  />
                </ReferenceLine>
              }
            </ComposedChart>
          </ResponsiveContainer>
        </ChartPanel>
      );
    }
    return <WarningMessage>No Stats Available</WarningMessage>;
  };

  return (
    <div>
      {renderStatsInfo()}
      {renderChart()}
      <OptionsPanel>
        {getBasedOn(
          filterStore.competitions[0],
          filterStore.configData.basedOn
        ).map((i) => (
          <OptionItem
            key={i.type}
            onClick={() => {
              handleOnClick(i.type);
            }}
            selected={chartBasedOn === i.type}
          >
            {getName(i.name)}
          </OptionItem>
        ))}
      </OptionsPanel>
    </div>
  );
}

export default Chart;
